import React from 'react'
import { Box, Stack, Fade, Typography, Tabs, Tab } from '@mui/material'
import LazyLoad from 'react-lazyload'
import { FormattedMessage } from 'react-intl'
import VideoModalPlayButton from '../VideoModalPlayButton'
import CompareImage from '../../utils/compareImage'
import { useState } from 'react'

interface IBlockFull2Col {
  image?: string
  title?: string
  subtitle?: string[]
  titleColor?: string
  iconImage?: string
  squareIcon?: boolean
  description?: string[]
  color?: string
  inverted?: boolean
  videoURL?: string
  imageCompare?: string
  leftImageLabel?: string
  rightImageLabel?: string
  children?: React.ReactNode
  bgColor?: string
  noPaddingTop?: boolean
  alt?: string
  altCompare?: string
  altIcon?: string
  dataGTM?: string
  isHeroHeader?: boolean
  defaultTabIndex?: number
  tabs?: {
    label: string
    disabled?: boolean
    image?: string
    title?: string
    subtitle?: string[]
    titleColor?: string
    iconImage?: string
    squareIcon?: boolean
    description?: string[]
    color?: string
    videoURL?: string
    imageCompare?: string
    leftImageLabel?: string
    rightImageLabel?: string
    children?: React.ReactNode
    noPaddingTop?: boolean
    alt?: string
    altCompare?: string
    altIcon?: string
  }[]
}

function styles(props: IBlockFull2Col) {
  return {
    box: {
      background: props.bgColor,
      px: { xs: 3, sm: 8, lg: 12 },
      py: { xs: 10, sm: 12, lg: 12 },
      pt: props.noPaddingTop ? { xs: 0, sm: 0, lg: 0 } : 'inherit'
    },
    boxWidth: {
      maxWidth: 1760,
      marginInline: 'auto'
    },
    container: {
      flexDirection: { xs: 'column', lg: props.inverted == true ? 'row-reverse' : 'row' },
      overflow: 'hidden',
      gap: { xs: 4, md: 4, lg: 10 }
    },
    imgContainer: {
      display: 'flex',
      flexBasis: { xs: '100%', md: '60%' },
      alignItems: 'center',
      aspectRatio: '16/9',
      position: 'relative',
      borderRadius: 3,
      overflow: 'hidden'
    },
    img: {
      height: '100%',
      width: '100%',
      objectFit: 'cover'
    },
    textContainer: {
      flexBasis: '40%',
      textAlign: 'left',
      gap: 2,
      alignItems: 'flex-start',
      justifyContent: 'center'
    },
    iconImage: {
      height: { xs: props.squareIcon ? 24 : 12, sm: props.squareIcon ? 32 : 14 }
    },
    title: {
      color: props.titleColor,
      mb: { xs: 0, lg: 2 }
    },
    subtitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: props.titleColor
    },
    description: {
      fontSize: '1.1rem',
      color: props.color
    },
    children: {
      flexDirection: 'row',
      gap: 2,
      mt: { xs: 1, sm: 2 },
      flexWrap: 'wrap',
      width: '100%'
    }
  }
}

const BlockFull2Col = (props: IBlockFull2Col) => {
  const {
    container,
    imgContainer,
    img,
    textContainer,
    iconImage,
    title,
    description,
    children,
    box,
    boxWidth,
    subtitle
  } = styles(props)

  const tabs = props.tabs ?? []
  const getInitialTabIndex = () => {
    if (tabs.length === 0) {
      return 0
    }
    if (props.defaultTabIndex !== undefined && props.defaultTabIndex >= 0 && props.defaultTabIndex < tabs.length) {
      return props.defaultTabIndex
    }
    return 0
  }
  const [tabIndex, setTabIndex] = useState(getInitialTabIndex)
  const isTabbed = tabs.length > 0
  const activeTab = isTabbed ? tabs[tabIndex] : null

  const TitleComponent = (props: IBlockFull2Col) => {
    const icon = isTabbed ? activeTab?.iconImage : props.iconImage
    const titleText = isTabbed ? activeTab?.title : props.title
    const altIconText = isTabbed ? activeTab?.altIcon : props.altIcon
    return (
      <>
        {icon && <Box component='img' alt={altIconText ?? 'Gemvision Software'} sx={{ ...iconImage }} src={icon} />}
        {titleText && (
          <Typography component='h2' variant='h1' sx={{ ...title, wordBreak: 'break-word' }}>
            <FormattedMessage id={titleText} />
          </Typography>
        )}
      </>
    )
  }

  const DescriptionComponent = (props: IBlockFull2Col) => {
    const subtitleList = isTabbed ? activeTab?.subtitle : props.subtitle
    const descriptionList = isTabbed ? activeTab?.description : props.description
    const childrenContent = isTabbed ? activeTab?.children : props.children

    return (
      <>
        {subtitleList &&
          subtitleList.map((item: string, index: number) => (
            <Typography sx={{ ...subtitle }} key={index}>
              <FormattedMessage id={item} />
            </Typography>
          ))}
        {descriptionList &&
          descriptionList.map((item: string, index: number) => (
            <Typography sx={{ ...description }} key={index}>
              <FormattedMessage id={item} />
            </Typography>
          ))}
        {childrenContent && <Stack sx={{ ...children }}>{childrenContent}</Stack>}
      </>
    )
  }

  const ImageComponent = (props: IBlockFull2Col) => {
    const image = isTabbed ? activeTab?.image : props.image
    const imageCompare = isTabbed ? activeTab?.imageCompare : props.imageCompare
    const leftLabel = isTabbed ? activeTab?.leftImageLabel : props.leftImageLabel
    const rightLabel = isTabbed ? activeTab?.rightImageLabel : props.rightImageLabel
    const videoURL = isTabbed ? activeTab?.videoURL : props.videoURL
    const alt = isTabbed ? activeTab?.alt : props.alt
    const altCompare = isTabbed ? activeTab?.altCompare : props.altCompare
    return (
      <Box sx={{ ...imgContainer }} role='img'>
        {!imageCompare ? (
          <Box component='img' sx={{ ...img }} src={image} alt={alt} />
        ) : (
          <CompareImage
            leftImageAlt={alt}
            rightImageAlt={altCompare ?? alt}
            leftImage={image ?? ''}
            rightImage={imageCompare ?? ''}
            rightImageLabel={rightLabel}
            leftImageLabel={leftLabel}
          />
        )}
        {videoURL && <VideoModalPlayButton videoURL={videoURL} />}
      </Box>
    )
  }

  const TabsComponent = () => {
    if (!isTabbed) return null
    return (
      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        variant='scrollable'
        scrollButtons='auto'
        sx={{ mb: 2 }}
      >
        {tabs.map((tab, index) => (
          <Tab
            disableRipple
            disabled={tab.disabled}
            key={`tab-${index}`}
            label={<FormattedMessage id={tab.label ?? ''} />}
            sx={{ fontSize: 16, textTransform: 'inherit' }}
          />
        ))}
      </Tabs>
    )
  }

  return (
    <Box component={props.isHeroHeader ? 'header' : 'section'} sx={{ ...box }}>
      <Box sx={{ ...boxWidth }}>
        <LazyLoad height={600} offset={300}>
          <Fade in={true} timeout={1000}>
            <Box data-gtm-click-subsection1={props.dataGTM}>
              <Stack sx={{ ...container, display: { xs: 'none', lg: 'flex' } }}>
                <Stack sx={{ ...textContainer }}>
                  <TabsComponent />
                  <TitleComponent {...props} />
                  <DescriptionComponent {...props} />
                </Stack>
                <ImageComponent {...props} />
              </Stack>

              <Stack sx={{ ...container, display: { xs: 'flex', lg: 'none' } }}>
                <Stack sx={{ ...textContainer }}>
                  <TabsComponent />
                  <TitleComponent {...props} />
                </Stack>
                <ImageComponent {...props} />
                <Stack sx={{ ...textContainer }}>
                  <DescriptionComponent {...props} />
                </Stack>
              </Stack>
            </Box>
          </Fade>
        </LazyLoad>
      </Box>
    </Box>
  )
}
export default BlockFull2Col
